import { Link } from 'react-router-dom';
import { customerSectionPath } from '../../lib/navigation.js';
import { PHASE, lifecycleNotice } from '../../lib/lifecycle-notice.js';
import LifecycleNotice, { MissingPermissionsNote } from '../../components/LifecycleNotice.jsx';
import ReadinessKpi from '../compliance/ReadinessKpi.jsx';
import { sourceStatuses, countStatuses } from '../../lib/data-source-states.js';
import { SOURCES_ID } from '../data-collection/SourcesCard.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Notice from '../../components/Notice.jsx';
import { trustStackNoticeState } from '../../components/TrustStackNotice.jsx';
import { AWS_DEPLOYMENT_TAB, codePushAttention } from '../../lib/deployment-health.js';
import LineChart from '../../components/LineChart.jsx';
import { useScoreHistory } from '../../lib/use-score-history.js';
import { formatPercent, formatScore } from '../../lib/score-format.js';
import { OnguardScoreKpi, SecureScoreKpi } from '../../components/ScoreKpis.jsx';
import { localDayKey, trendCaption, trendSummary } from '../../lib/score-history.js';
import { useChangesSinceYesterday } from '../../lib/use-changes-feed.js';
import { AREA_LABELS, categoryOfFinding } from '../../lib/finding-category.js';
import CisNotice from '../../components/CisNotice.jsx';
import { CIS_FRAMEWORK_ID } from '../../../../shared/compliance/engine/notices.mjs';

/**
 * The customer's Overview (ENTRAID-347 redesign, mockup 1, implementation plan C1). Top to bottom:
 *   the page header; one "Needs attention" list (lifecycle, missing permissions, new sources, a failed collection);
 *   4 KPI cards (onGuard score, Microsoft Secure Score, Readiness, Collections today), each a link to its detail;
 *   one wide card with the 30-day onGuard score line and "Since the last score change" (the newest run where the
 *   score moved, and why: score before and after, date, the checks that changed status, the Secure Score delta, plus
 *   Watch items for viewers who may read them; or since when the score is unchanged);
 *   "Fix first": the 3 most severe failing checks of the latest collection.
 * While the lifecycle notice stands and no collection exists, the empty KPIs, trend and Fix first stay hidden.
 * `historyAllowed` is true only for viewers who may read GET /customers/{id}/runs (operator, the customer's own admin or
 * its viewer role): for anyone else the trend is not requested and not shown, without an error.
 * `readOnly` is the viewer role (read-only Overview and Security, 2026-10-25): the same facts, but no action and no link
 * to a page it cannot open (only Overview and Security stay links); the API refuses it everything else regardless.
 */

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
const SEVERITY_ORDER = Object.freeze({ critical: 0, high: 1, medium: 2, low: 3, info: 4 });
const SEVERITY_WORD = Object.freeze({ critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' });
const SEVERITY_DOT = Object.freeze({ critical: 'var(--bad)', high: 'var(--bad)', medium: 'var(--warn-dot)', low: 'var(--track)', info: 'var(--track)' });
const TONE_DOT = Object.freeze({ bad: 'var(--bad)', warn: 'var(--warn-dot)', info: 'var(--blue)', good: 'var(--good)' });
const REF_SHORT = Object.freeze({ 'cis-m365': 'CIS', 'nist-800-53': 'NIST' });
export const FIX_FIRST_COUNT = 3;

/**
 * The onGuard score caption without a trend: the issues to fix and, only when the previous collection also has a
 * score, the change since it. Never a delta without two real scores.
 */
export function scoreCaption(run, previousRun) {
  const fails = run?.securityPosture?.failCount;
  const issues = finite(fails) ? `${fails} ${fails === 1 ? 'issue' : 'issues'} to fix` : null;
  const now = run?.securityPosture?.score;
  const before = previousRun?.securityPosture?.score;
  let change = null;
  if (finite(now) && finite(before)) {
    const d = now - before;
    const pts = (n) => `${n} ${n === 1 ? 'point' : 'points'}`;
    change = d === 0 ? 'no change since the previous collection' : `${d > 0 ? 'up' : 'down'} ${pts(Math.abs(d))} since the previous collection`;
  }
  if (issues && change) return `${issues}, ${change}`;
  if (change) return change.charAt(0).toUpperCase() + change.slice(1);
  return issues ?? 'Read from your last collection';
}

const timeOf = (t) => new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/** "today at 6:02 AM", "yesterday at 6:02 AM" or "20 Oct at 6:02 AM" (local time). */
export function collectionWhen(ts, now = Date.now()) {
  const t = Date.parse(ts ?? '');
  if (!Number.isFinite(t)) return null;
  const day = localDayKey(t);
  if (day === localDayKey(now)) return `today at ${timeOf(t)}`;
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (day === localDayKey(y)) return `yesterday at ${timeOf(t)}`;
  return `${new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).replace('Sept', 'Sep')} at ${timeOf(t)}`;
}

/** The top failing checks, most severe first (stable for equal severity). */
export function fixFirst(findings, n = FIX_FIRST_COUNT) {
  return (Array.isArray(findings) ? findings : [])
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => f?.status === 'fail')
    .sort((a, b) => ((SEVERITY_ORDER[a.f.severity] ?? 5) - (SEVERITY_ORDER[b.f.severity] ?? 5)) || (a.i - b.i))
    .slice(0, n)
    .map(({ f }) => f);
}

const RUN_WORD = (code) => (code === 200 ? 'Complete' : code === 207 ? 'Partial' : code === 206 ? 'Timed out' : 'Failed');
const RUN_TONE = (code) => (code === 200 ? 'good' : code === 207 || code === 206 ? 'warn' : 'bad');

function Dot({ color, className = '' }) {
  return <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${className}`} style={{ background: color }} />;
}

// The sections a read-only viewer can open; every other section link renders as plain text for it.
const READ_ONLY_SECTIONS = new Set(['overview', 'security']);

/** A link, or the same text without one when `to` is null (a page this user cannot open). */
function MaybeLink({ to, children, ...rest }) {
  return to ? <Link to={to} {...rest}>{children}</Link> : <span className={rest.className}>{children}</span>;
}

export default function OverviewPage({ customer, config, recentRuns, audience, onRefresh, complianceRole = null, marketplaceListingUrl = '', watchAllowed = false, historyAllowed = false, readOnly = false }) {
  const customerId = customer.customerId;
  const lastRun = recentRuns?.[0];
  const notice = lifecycleNotice(customer, { audience, hasData: !!lastRun });
  const waiting = Boolean(notice) && !lastRun;
  const to = (section) => (readOnly && !READ_ONLY_SECTIONS.has(section) ? null : customerSectionPath(customerId, section));
  const { status: historyStatus, history } = useScoreHistory(customerId, { enabled: historyAllowed && !waiting });
  const showTrend = historyAllowed && historyStatus !== 'forbidden';
  // The server computes the feed (ENTRAID-347 D2); recentRuns (whole rows) only feed the fallback if it cannot be read.
  const feed = useChangesSinceYesterday(customerId, { runs: recentRuns ?? [], watchAllowed: watchAllowed && !waiting, enabled: !waiting, mode: 'last-score-change' });

  const name = customer.companyName || customerId;
  const when = lastRun ? collectionWhen(lastRun.runTimestamp) : null;
  const subline = when
    ? `${name} tenant health. Last collection ${when}.`
    : `${name} tenant health. ${notice?.phase === PHASE.FIRST_COLLECTION ? 'The first collection is running.' : 'No collection yet.'}`;

  return (
    <div>
      <PageHeader title="Overview" subline={subline} />
      <NeedsAttention customer={customer} config={config} lastRun={lastRun} audience={audience} onRefresh={onRefresh} to={to} readOnly={readOnly} />
      {waiting ? null : (
        <div className="space-y-4">
          <OverviewKpis
            customer={customer} config={config} lastRun={lastRun} previousRun={recentRuns?.[1]} history={history}
            to={to} complianceRole={complianceRole} marketplaceListingUrl={marketplaceListingUrl}
          />
          <TrendAndChanges showTrend={showTrend} history={history} historyStatus={historyStatus} lastRun={lastRun} feed={feed} to={to} customerId={customerId} />
          <FixFirst findings={lastRun?.securityPosture?.findings} to={to} />
        </div>
      )}
    </div>
  );
}

/** One notice list above the KPI row: lifecycle, missing permissions, a failed collector update, an outdated access role, new sources and a failed collection. */
function NeedsAttention({ customer, config, lastRun, audience, onRefresh, to, readOnly = false }) {
  // Turning sources on and granting permissions are admin actions: a read-only viewer is not asked to do them.
  const newSources = readOnly ? 0 : config?.availableNotEnabled?.length ?? 0;
  const failed = lastRun && lastRun.statusCode != null && lastRun.statusCode !== 200;
  const lifecycle = lifecycleNotice(customer, { audience, hasData: !!lastRun });
  const pushAttention = codePushAttention(customer);
  const trustState = trustStackNoticeState(customer);
  const deploymentPath = customerSectionPath(customer.customerId, AWS_DEPLOYMENT_TAB);
  const items = [
    lifecycle ? <LifecycleNotice key="lifecycle" customer={customer} audience={audience} hasData={!!lastRun} onRefresh={onRefresh} readOnly={readOnly} /> : null,
    !readOnly && customer?.missingPermissions?.length > 0 ? <MissingPermissionsNote key="permissions" customer={customer} onRefresh={onRefresh} /> : null,
    // A failed collector update and an outdated access role: told whole, one action each to Settings > AWS deployment.
    // Not for the read-only viewer, who cannot act on them (and cannot open Settings).
    !readOnly && pushAttention ? (
      <Notice
        key="code-push"
        tone="bad"
        title={pushAttention.title}
        titleAs="p"
        testId="code-push-attention"
        action={<Link to={deploymentPath} className="text-sm font-semibold text-ipc-light hover:underline">{pushAttention.action}</Link>}
      >
        {pushAttention.body}
      </Notice>
    ) : null,
    !readOnly && trustState ? (
      <Notice
        key="trust-stack"
        tone="warn"
        title="Update the onGuard access role in your AWS account"
        titleAs="p"
        testId="trust-stack-attention"
        action={<Link to={deploymentPath} className="text-sm font-semibold text-ipc-light hover:underline">Open AWS deployment</Link>}
      >
        {trustState.outdated
          ? `Your AWS account has an older onGuard access role (version ${trustState.version ?? 'unknown'}). It can manage every onGuard collector in the account, not only yours, and onGuard cannot limit it to your own collector until it is updated. onGuard keeps working in the meantime. Someone who can update AWS CloudFormation stacks in your account updates it in about a minute; the steps are on the AWS deployment page.`
          : `The onGuard access role in your AWS account is set up for another collector name, so it cannot manage your collector, ${trustState.clientName}, and collector updates will fail until it is corrected. Someone who can update AWS CloudFormation stacks in your account corrects it in about a minute; the steps are on the AWS deployment page.`}
      </Notice>
    ) : null,
    newSources > 0 ? (
      <Notice
        key="sources"
        tone="info"
        title={newSources === 1 ? '1 new data source is available' : `${newSources} new data sources are available`}
        titleAs="p"
        testId="new-sources-line"
        action={<Link to={`${to('data-collection')}#sources`} className="text-sm font-semibold text-ipc-light hover:underline">Review in Data collection</Link>}
      >
        Turn them on to collect more of your tenant.
      </Notice>
    ) : null,
    failed ? (
      <Notice
        key="collection"
        tone={RUN_TONE(lastRun.statusCode)}
        title={lastRun.statusCode === 207 ? 'The last collection was partial' : lastRun.statusCode === 206 ? 'The last collection timed out' : 'The last collection failed'}
        titleAs="p"
        role="note"
        testId="collection-attention"
        action={to('data-collection') ? <Link to={to('data-collection')} className="text-sm font-semibold text-ipc-light hover:underline">Open Data collection</Link> : null}
      >
        {(lastRun.failed ?? []).length > 0
          ? `Not collected: ${lastRun.failed.map((f) => (typeof f === 'string' ? f : f?.name)).filter(Boolean).join(', ')}.`
          : 'Some data may be missing from this collection.'}
      </Notice>
    ) : null,
  ].filter(Boolean);
  if (items.length === 0) return null;
  return <div className="mb-4 flex flex-col gap-3 [&>*]:!mt-0" data-testid="needs-attention">{items}</div>;
}

function OverviewKpis({ customer, config, lastRun, previousRun, history, to, complianceRole, marketplaceListingUrl }) {
  const secScore = lastRun?.securityPosture?.score;
  const msScore = lastRun?.securityPosture?.msSecureScore;
  // One square per source in scope (ENTRAID-347): collected, pending (license, permission, not run yet) or failed.
  const sources = lastRun ? sourceStatuses(config, lastRun) : [];
  const counts = countStatuses(sources);
  const succeeded = counts.collected;
  const total = sources.length;
  const today = lastRun && localDayKey(lastRun.runTimestamp) === localDayKey(Date.now());
  // The pending sources (a license, a permission, or not run yet) are a row of their own that opens the sources that
  // need a license or a permission on Data collection, so the caption only says when the run finished.
  const runCaption = lastRun
    ? `${RUN_WORD(lastRun.statusCode)} ${today ? `at ${timeOf(Date.parse(lastRun.runTimestamp))}` : collectionWhen(lastRun.runTimestamp)}`
    : 'No collection yet';
  const needsAccessTo = to('data-collection') ? `${to('data-collection')}#${SOURCES_ID}` : undefined;
  const collectionRows = [
    { key: 'completed', count: counts.collected, label: 'completed', tone: counts.collected > 0 ? 'good' : 'none' },
    { key: 'failed', count: counts.failed, label: 'failed', tone: counts.failed > 0 ? 'bad' : 'none' },
    {
      key: 'pending',
      count: counts.pending,
      label: 'pending (license or permission)',
      tone: counts.pending > 0 ? 'warn' : 'none',
      to: counts.pending > 0 ? needsAccessTo : undefined,
      linkLabel: `${counts.pending} pending (license or permission). Open the sources that need a license or a permission on Data collection`,
    },
  ];
  const collectionsLabel = today || !lastRun ? 'Collections today' : 'Last collection';

  return (
    <KpiGrid max={4} data-testid="overview-kpis">
      <OnguardScoreKpi
        score={secScore}
        to={{ pathname: to('security'), search: new URLSearchParams({ tab: 'findings' }).toString() }}
        ariaLabel={`onGuard score: ${formatPercent(secScore)}. Open the Security findings`}
        caption={trendCaption(history) ?? scoreCaption(lastRun, previousRun)}
      />
      <SecureScoreKpi
        msScore={msScore}
        to={to('security')}
        ariaLabel={`Microsoft Secure Score: ${formatPercent(msScore?.percentage)}. Open the Microsoft Secure Score on the Security page`}
      />
      <ReadinessKpi customerId={customer.customerId} role={complianceRole} tier={customer.tierName} tierKnown={Boolean(customer.tierName)} marketplaceListingUrl={marketplaceListingUrl} />
      {/* No per-source squares here (product owner, 2026-10-22): the summary rows; the squares stay on Data collection.
          The pending row is a link, so the card itself is not one (no nested links); the label opens Data collection. */}
      <KpiCard
        label={<MaybeLink to={to('data-collection')} className="hover:underline">{collectionsLabel}</MaybeLink>}
        kpi="collections"
        ariaLabel={lastRun ? `${collectionsLabel}: ${succeeded} of ${total} sources collected, ${counts.pending} pending, ${counts.failed} failed, ${RUN_WORD(lastRun.statusCode)}` : `${collectionsLabel}: none yet`}
        value={lastRun ? succeeded : null}
        unit={`of ${total}`}
        variant="summary"
        rows={collectionRows}
        tone={lastRun ? RUN_TONE(lastRun.statusCode) : undefined}
        caption={runCaption}
      />
    </KpiGrid>
  );
}

function TrendAndChanges({ showTrend, history, historyStatus, lastRun, feed, to, customerId }) {
  if (!showTrend) {
    return (
      <section className="card min-w-0" aria-label="Changes" data-testid="trend-and-changes">
        <SinceLastScoreChange feed={feed} to={to} customerId={customerId} />
      </section>
    );
  }
  const covers = history.coversDays >= 30 ? 30 : history.coversDays;
  const title = history.points.length >= 2 ? `onGuard score, last ${covers} ${covers === 1 ? 'day' : 'days'}` : 'onGuard score trend';
  const summary = trendSummary(history);
  const current = lastRun?.securityPosture?.score;
  return (
    <section className="card grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr] lg:gap-0" aria-label="Trend and changes" data-testid="trend-and-changes">
      <div className="min-w-0 lg:pr-7">
        <div className="mb-3.5 flex flex-wrap items-baseline gap-x-3">
          <h2>{title}</h2>
          <p className="text-[13.5px] text-ipc-muted">One point per day</p>
        </div>
        {historyStatus === 'loading' && history.points.length === 0 ? (
          <div className="h-48 rounded-lg bg-ipc-track motion-safe:animate-pulse sm:h-56" role="status"><span className="sr-only">Loading the score history</span></div>
        ) : historyStatus === 'error' ? (
          <p className="text-sm text-ipc-muted" role="note">The score history could not be loaded. The current score is on the card above.</p>
        ) : summary ? (
          <LineChart series={[{ name: 'onGuard score', points: history.points.map((p) => ({ day: p.day, value: p.score })), format: (v) => formatScore(Math.round(v)) }]} summary={summary} todayKey={history.today} testId="score-trend" />
        ) : (
          <p className="text-sm text-ipc-muted" data-testid="trend-empty">
            The trend appears after your second daily collection.{finite(current) ? ` Today's onGuard score is ${formatScore(current)}.` : ''}
          </p>
        )}
      </div>
      <div className="min-w-0 border-t border-ipc-border pt-6 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
        <SinceLastScoreChange feed={feed} to={to} customerId={customerId} />
      </div>
    </section>
  );
}

// Section names a changes row may still carry from a portal deployed before the ENTRAID-347 menu, and where they live now.
const MOVED_SECTIONS = Object.freeze({ notifications: 'activity', collections: 'data-collection', sources: 'data-collection' });

// Null when the row's page is one this user cannot open (`to` answered null): the row is then plain text.
function targetPath(target, customerId, to) {
  if (target?.eventId) {
    const activity = to('activity');
    return activity ? `${activity}/events/${encodeURIComponent(target.eventId)}` : null;
  }
  const section = target?.section ?? 'overview';
  const path = to(MOVED_SECTIONS[section] ?? section);
  if (!path) return null;
  return target?.search ? { pathname: path, search: new URLSearchParams(target.search).toString() } : path;
}

const dateText = (iso) => {
  const t = Date.parse(iso ?? '');
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
};
const signed = (d, digits = 0) => `${d > 0 ? '+' : ''}${d.toFixed(digits)}`;
const CHECK_GROUPS = Object.freeze([
  { to: 'fail', label: 'Newly failing', tone: 'bad' },
  { to: 'warn', label: 'Now warning', tone: 'warn' },
  { to: 'pass', label: 'Newly passing', tone: 'good' },
]);
// Rows the explanation already covers (score, Secure Score and checks of the change); the rest (Watch, the latest
// collection) still show under it.
const EXPLAINED_ROW = (id) => id === 'score' || id === 'secure-score' || id.startsWith('drift-');

/** Why the score moved: before and after, the date, the checks that changed status in that run, the Secure Score. */
function ScoreChangeExplained({ change, to }) {
  const s = change.score;
  const ss = change.secureScore;
  const checks = Array.isArray(change.checks) ? change.checks : [];
  const checkTo = (id) => ({ pathname: to('security'), search: new URLSearchParams({ tab: 'findings', check: id }).toString() });
  return (
    <div data-testid="score-change" className="space-y-2">
      {s && finite(s.from) && finite(s.to) ? (
        <p className="flex items-start gap-3">
          <Dot color={s.delta < 0 ? TONE_DOT.bad : TONE_DOT.good} className="mt-1.5" />
          <span>
            <span className="font-semibold text-ipc-ink">onGuard score {formatScore(s.from)} to {formatScore(s.to)}</span>{' '}
            <span className="text-[13px] text-ipc-muted">({signed(s.delta)} {Math.abs(s.delta) === 1 ? 'point' : 'points'})</span>
            <span className="block text-[13px] text-ipc-muted">Collection of {dateText(change.at)}, compared with {dateText(change.previousAt)}.</span>
          </span>
        </p>
      ) : null}
      {ss && finite(ss.delta) && ss.delta !== 0 ? (
        <p className="text-[13px] text-ipc-muted" data-testid="score-change-secure-score">
          Microsoft Secure Score {ss.from.toFixed(1)} to {ss.to.toFixed(1)}{finite(ss.max) ? ` of ${ss.max}` : ''} ({signed(ss.delta, 1)}).
        </p>
      ) : null}
      {checks.length === 0 ? (
        <p className="text-[13px] text-ipc-muted">No check changed status in that collection.</p>
      ) : CHECK_GROUPS.map((g) => {
        const list = checks.filter((c) => c.to === g.to);
        if (list.length === 0) return null;
        return (
          <div key={g.to} data-check-group={g.to}>
            <h3 className="text-[13px] font-semibold text-ipc-ink">{g.label}</h3>
            <ul className="mt-1 space-y-1">
              {list.map((c) => (
                <li key={c.id} className="flex items-start gap-2 text-[13px]">
                  <Dot color={TONE_DOT[g.tone]} className="mt-1.5 !h-2 !w-2" />
                  <span className="min-w-0">
                    <Link to={checkTo(c.id)} className="font-medium text-ipc-ink hover:text-ipc-light hover:underline">{c.title}</Link>
                    {c.severity ? <span className="ml-2 text-xs font-semibold text-ipc-muted" data-severity={c.severity}>{SEVERITY_WORD[c.severity] ?? c.severity}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {finite(change.checksTotal) && change.checksTotal > checks.length ? (
        <p className="text-[13px] text-ipc-muted">Showing {checks.length} of {change.checksTotal} changed checks.</p>
      ) : null}
    </div>
  );
}

function ChangeRows({ rows, customerId, to }) {
  return (
    <ul className="divide-y divide-ipc-border">
      {rows.map((r) => (
        <li key={r.id} className="flex items-start gap-3 py-3 first:pt-1" data-change={r.id}>
          <Dot color={TONE_DOT[r.tone] ?? TONE_DOT.info} className="mt-1.5" />
          <div className="min-w-0">
            <MaybeLink to={targetPath(r.target, customerId, to)} className="font-semibold text-ipc-ink hover:text-ipc-light hover:underline">{r.title}</MaybeLink>
            {r.detail ? <p className="mt-0.5 break-words text-[13px] text-ipc-muted">{r.detail}</p> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * "Since the last score change" (ENTRAID-347, Hugo 2026-10-21). The server finds the newest run where the onGuard
 * score moved and explains it; with no change in the history it says since when the score is unchanged. Watch and
 * collection rows show under it (Watch only for viewers the server allows). If the feed cannot be read, the client
 * fallback rows show instead.
 */
function SinceLastScoreChange({ feed, to, customerId }) {
  const change = feed.lastScoreChange;
  const rows = change ? feed.rows.filter((r) => !EXPLAINED_ROW(r.id)) : feed.rows;
  return (
    <div data-testid="last-score-change">
      <div className="mb-3.5 flex flex-wrap items-baseline gap-x-3">
        <h2>Since the last score change</h2>
        {to('activity') ? <Link to={to('activity')} className="tap ml-auto inline-flex items-center text-[13.5px] font-semibold text-ipc-light hover:underline">See all activity</Link> : null}
      </div>
      {change?.status === 'changed' ? <ScoreChangeExplained change={change} to={to} /> : null}
      {change?.status === 'unchanged' ? (
        <p className="text-sm text-ipc-muted" data-testid="score-unchanged">Score unchanged since {dateText(change.since)}.</p>
      ) : null}
      {rows.length > 0 ? (
        <div className={change?.status === 'changed' || change?.status === 'unchanged' ? 'mt-3 border-t border-ipc-border pt-2' : ''}>
          <ChangeRows rows={rows} customerId={customerId} to={to} />
        </div>
      ) : !change || change.status === 'no-runs' ? (
        <p className="text-sm text-ipc-muted">No score change to explain yet.</p>
      ) : null}
      {!change && feed.total > feed.rows.length ? (
        <p className="mt-2 text-[13px] text-ipc-muted">Showing {feed.rows.length} of {feed.total} changes.</p>
      ) : null}
      {feed.watchHint && to('notifications') ? (
        <p className="mt-3 text-[13px] text-ipc-muted" data-testid="watch-hint">
          <Link to={`${to('notifications')}/rules`} className="font-semibold text-ipc-light hover:underline">Turn on Watch</Link> to see admin, guest and sign-in changes.
        </p>
      ) : null}
    </div>
  );
}

// True when a listed check shows a CIS control reference (its labels come from the first two evaluated references).
const showsCisRef = (findings) => findings.some((f) => (Array.isArray(f?.controlRefs?.evaluates) ? f.controlRefs.evaluates : []).filter((r) => r && typeof r.controlId === 'string').slice(0, 2).some((r) => r.framework === CIS_FRAMEWORK_ID));

function refLabels(f) {
  const list = Array.isArray(f?.controlRefs?.evaluates) ? f.controlRefs.evaluates : [];
  return list.filter((r) => r && typeof r.controlId === 'string').slice(0, 2).map((r) => `${REF_SHORT[r.framework] ?? r.framework} ${r.controlId}`);
}

function FixFirst({ findings, to }) {
  if (!Array.isArray(findings)) return null;
  const top = fixFirst(findings);
  const securityTo = { pathname: to('security'), search: new URLSearchParams({ tab: 'findings' }).toString() };
  return (
    <SectionCard
      title="Fix first"
      description="The failing checks that matter most"
      action={<Link to={to('security')} className="text-ipc-light hover:underline">Open Security</Link>}
      testId="fix-first"
    >
      {top.length === 0 ? (
        <p className="text-sm text-ipc-muted">No failing checks on the latest collection.</p>
      ) : (
        <ol className="divide-y divide-ipc-border">
          {top.map((f, i) => {
            const area = AREA_LABELS[categoryOfFinding(f.id)];
            const refs = refLabels(f);
            return (
              <li key={f.id ?? i} className="flex flex-col gap-2 py-3 first:pt-1 sm:flex-row sm:items-start sm:gap-3" data-fix={f.id}>
                <span aria-hidden="true" className="w-6 shrink-0 font-display text-lg font-extrabold leading-tight text-ipc-primary">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <Link to={{ pathname: securityTo.pathname, search: new URLSearchParams({ tab: 'findings', ...(f.id ? { check: f.id } : {}) }).toString() }} className="font-semibold text-ipc-ink hover:text-ipc-light hover:underline">{f.title}</Link>
                  {refs.length ? (
                    <span className="ml-2 inline-flex flex-wrap gap-1.5 align-middle">
                      {refs.map((r) => <span key={r} className="rounded border border-ipc-border px-1.5 text-[11.5px] font-medium text-ipc-muted">{r}</span>)}
                    </span>
                  ) : null}
                  {f.remediation ? <p className="mt-0.5 text-[13px] text-ipc-muted">{f.remediation}</p> : null}
                </div>
                <span className="flex shrink-0 items-center gap-3 whitespace-nowrap text-[13px] text-ipc-muted sm:ml-auto">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ipc-ink" data-severity={f.severity}>
                    <Dot color={SEVERITY_DOT[f.severity] ?? SEVERITY_DOT.info} className="!h-2 !w-2" />
                    {SEVERITY_WORD[f.severity] ?? f.severity}
                  </span>
                  {area ? <span>{area}</span> : null}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {top.length > 0 && showsCisRef(top) ? (
        <CisNotice className="pt-2 text-xs text-ipc-muted" />
      ) : null}
    </SectionCard>
  );
}
